import React, {
  FunctionComponent,
  useState,
  useContext,
  useEffect,
} from 'react';
import { Box, TextField, Tooltip } from '@mui/material';
import { IClaimForm, IClaim } from '@src/types/claimsTracker';
import { Rol } from '@src/types/common';
import { IInspectionContext, InspectionContext } from '@context/inspections';
import { ActionType } from '@src/types/action';
import ClaimsTrackerClient from '@shared/clients/ClaimsTrackerClient';
import Button from '../button';
import './index.scss';

interface ReserveProps {
  claimUId: string;
  claimForm?: IClaimForm;
  legacyCurrentClaim?: IClaim;
}

const Reserve: FunctionComponent<ReserveProps> = ({
  claimUId,
  claimForm,
  legacyCurrentClaim,
}): JSX.Element => {
  const { dispatch } = useContext<IInspectionContext>(InspectionContext);

  // State
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [initialReserve, setInitialReserve] = useState<number>(0);
  const [updatedReserve, setUpdatedReserve] = useState<number>(0);
  const [editValue, setEditValue] = useState<string>('');
  const [validationError, setValidationError] = useState<string>('');

  // Permissions
  const userRole = Number(localStorage.getItem('rolId'));
  const canEdit = userRole === Rol.ADMIN || userRole === Rol.ADJUSTER;

  // State validation for the EDIT button visibility
  const currentStepStatus = claimForm?.info?.currentStepStatus ?? '';

  const isFinalized = [
    'FINALIZED_PAYMENT',
    'FINALIZED_REPAIR',
    'FINALIZED',
    'CANCELLED',
    'DELETED',
    'OBJETED_CLOSURE',
    'FINALIZED_AUDIT',
    'FINALIZED_WITHOUT_ESTIMATED',
    'COMPLETE',
  ].includes(currentStepStatus);

  // The edit button is shown (enabled) only when state is >= Análisis de Daños and NOT finalized.
  // The component ALWAYS renders both fields regardless of state.
  // The backend validates state independently (409) for the two-tab scenario.
  const currentStep = claimForm?.info?.currentStep ?? '';
  const stepsAllowingEdit = [
    'DAMAGE_ANALYSIS',
    'OPTIONS_AGREEMENT',
    'INSURANCE_PROCESSED',
    'SINISTER_SECTION',
  ];
  const isInEditableStage = stepsAllowingEdit.includes(currentStep);
  const showEditButton = canEdit && isInEditableStage && !isFinalized;

  // Load data from the event JSON field data
  useEffect(() => {
    const eventData = legacyCurrentClaim?.jsonFieldData?.event;
    if (eventData) {
      const reserve = Number(eventData.initialReserve) || 0;
      setInitialReserve(reserve);

      const updated = Number(eventData.updatedReserve) || 0;
      setUpdatedReserve(updated);
    }
  }, [legacyCurrentClaim?.jsonFieldData?.event]);

  const formatCurrency = (value: number): string => {
    if (value === null || value === undefined || isNaN(value)) return '0.00';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const handleEdit = () => {
    setEditValue(updatedReserve > 0 ? updatedReserve.toString() : '');
    setValidationError('');
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue('');
    setValidationError('');
  };

  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value.replace(/[^0-9.]/g, '');

    // Prevent multiple dots
    const parts = raw.split('.');
    const sanitized =
      parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : raw;

    setEditValue(sanitized);
    setValidationError('');
  };

  const validate = (value: string): boolean => {
    const numericValue = parseFloat(value);

    if (!value || value.trim() === '') {
      setValidationError('El monto es requerido.');
      return false;
    }

    if (isNaN(numericValue)) {
      setValidationError('Ingrese un valor numérico válido.');
      return false;
    }

    if (numericValue <= 0) {
      setValidationError('El monto debe ser mayor que cero.');
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validate(editValue)) return;

    const numericValue = parseFloat(editValue);

    // If value hasn't changed, just exit edit mode without calling the API
    // Use tolerance for floating point comparison
    if (Math.abs(numericValue - updatedReserve) < 0.001) {
      setIsEditing(false);
      setEditValue('');
      return;
    }

    setIsSaving(true);

    try {
      const client = new ClaimsTrackerClient();
      const result = await client.updateReserve(claimUId, numericValue);

      if (result?.data) {
        const newValue = result.data.reserveUpdated ?? numericValue;
        setUpdatedReserve(newValue);
        setIsEditing(false);
        setEditValue('');

        dispatch({
          type: ActionType.SET_GENERAL_ALERT,
          payload: {
            showAlert: true,
            alertMessage: 'Reserva actualizada exitosamente.',
            alertType: 'success',
          },
        });
      }
    } catch (error: any) {
      const statusCode = error?.response?.status;
      let errorMessage = 'Error al actualizar la reserva. Intente nuevamente.';

      if (statusCode === 409) {
        errorMessage =
          error?.response?.data?.detail ||
          'El estado actual de la reclamación no permite editar la reserva.';
      } else if (statusCode === 400) {
        errorMessage =
          error?.response?.data?.detail || 'El monto ingresado no es válido.';
      } else {
        errorMessage =
          error?.response?.data?.detail ||
          error?.response?.data?.title ||
          errorMessage;
      }

      dispatch({
        type: ActionType.SET_GENERAL_ALERT,
        payload: {
          showAlert: true,
          alertMessage: errorMessage,
          alertType: 'error',
        },
      });

      // Revert to previous value
      setIsEditing(false);
      setEditValue('');
    } finally {
      setIsSaving(false);
    }
  };

  const getEditButtonTooltip = (): string => {
    if (isFinalized) {
      return 'La reclamación se encuentra en estado Finalizado. No es posible editar la reserva.';
    }
    return '';
  };

  return (
    <Box className="reserve-section">
      <Box className="reserve-section__fields">
        <Box className="reserve-section__field">
          <TextField
            label="Reserva inicial"
            value={formatCurrency(initialReserve)}
            disabled
            fullWidth
            size="small"
            InputProps={{ readOnly: true }}
            aria-label="Reserva inicial"
          />
        </Box>

        <Box className="reserve-section__field">
          {isEditing ? (
            <TextField
              label="Reserva actualizada"
              value={editValue}
              onChange={handleValueChange}
              fullWidth
              size="small"
              autoFocus
              error={!!validationError}
              helperText={validationError}
              disabled={isSaving}
              inputProps={{
                inputMode: 'decimal',
                'aria-label': 'Reserva actualizada',
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave();
                if (e.key === 'Escape') handleCancel();
              }}
            />
          ) : (
            <TextField
              label="Reserva actualizada"
              value={formatCurrency(updatedReserve)}
              disabled
              fullWidth
              size="small"
              InputProps={{ readOnly: true }}
              aria-label="Reserva actualizada"
            />
          )}
        </Box>

        <Box className="reserve-section__actions">
          {isEditing ? (
            <>
              <Button
                text={isSaving ? 'GUARDANDO...' : 'GUARDAR'}
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={isSaving}
              />
              <Button
                text="CANCELAR"
                variant="outlined"
                color="secondary"
                onClick={handleCancel}
                disabled={isSaving}
              />
            </>
          ) : (
            showEditButton && (
              <Tooltip title={getEditButtonTooltip()} arrow>
                <span>
                  <Button
                    text="EDITAR"
                    variant="outlined"
                    onClick={handleEdit}
                  />
                </span>
              </Tooltip>
            )
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Reserve;
